// Client API tipizzato per le route /api/admin/flows.
// Validation Zod sui response per garantire shape coerente con backend.

import {
  FlowSchema,
  FlowWithRelationsSchema,
  FlowQuestionSchema,
  FlowOutcomeSchema,
  VisualLayoutSchema,
  type Flow,
  type FlowWithRelations,
  type FlowCreate,
  type FlowUpdate,
  type FlowQuestion,
  type FlowQuestionCreate,
  type FlowQuestionUpdate,
  type FlowOutcome,
  type FlowOutcomeCreate,
  type FlowOutcomeUpdate,
  type VisualLayout,
} from '@audiq/shared';
import { z } from 'zod';
import { request } from './_request';

const FlowsListResponseSchema = z.object({
  ok: z.literal(true),
  total: z.number().int(),
  page: z.number().int(),
  per_page: z.number().int(),
  flows: z.array(FlowSchema),
});

export async function listFlows(params?: {
  page?: number;
  per_page?: number;
  search?: string;
  include_archived?: boolean;
}): Promise<{ total: number; page: number; per_page: number; flows: Flow[] }> {
  const search = new URLSearchParams();
  if (params?.page) search.set('page', String(params.page));
  if (params?.per_page) search.set('per_page', String(params.per_page));
  if (params?.search) search.set('search', params.search);
  if (params?.include_archived) search.set('include_archived', 'true');
  const qs = search.toString() ? `?${search}` : '';
  const res = await request<unknown>(`/api/admin/flows${qs}`);
  return FlowsListResponseSchema.parse(res);
}

const FlowResponseSchema = z.object({ ok: z.literal(true), flow: FlowSchema });
const FlowWithRelationsResponseSchema = z.object({
  ok: z.literal(true),
  flow: FlowWithRelationsSchema,
});

export async function createFlow(data: FlowCreate): Promise<Flow> {
  const res = await request<unknown>('/api/admin/flows', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return FlowResponseSchema.parse(res).flow;
}

export async function getFlow(id: string): Promise<FlowWithRelations> {
  const res = await request<unknown>(`/api/admin/flows/${id}`);
  return FlowWithRelationsResponseSchema.parse(res).flow;
}

export async function updateFlow(id: string, data: FlowUpdate): Promise<Flow> {
  const res = await request<unknown>(`/api/admin/flows/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return FlowResponseSchema.parse(res).flow;
}

export async function deleteFlow(id: string): Promise<void> {
  await request(`/api/admin/flows/${id}`, { method: 'DELETE' });
}

export async function publishFlow(id: string): Promise<{ version_number: number }> {
  const res = await request<unknown>(`/api/admin/flows/${id}/publish`, {
    method: 'POST',
  });
  return z.object({ ok: z.literal(true), version_number: z.number().int() }).parse(res);
}

export async function duplicateFlow(id: string): Promise<Flow> {
  const res = await request<unknown>(`/api/admin/flows/${id}/duplicate`, {
    method: 'POST',
  });
  return FlowResponseSchema.parse(res).flow;
}

export async function getPreviewPrompt(id: string): Promise<string> {
  const res = await request<unknown>(`/api/admin/flows/${id}/preview-prompt`);
  return z.object({ ok: z.literal(true), prompt: z.string() }).parse(res).prompt;
}

// ----- Questions -----

const QuestionsListResponseSchema = z.object({
  ok: z.literal(true),
  questions: z.array(FlowQuestionSchema),
});
const QuestionResponseSchema = z.object({
  ok: z.literal(true),
  question: FlowQuestionSchema,
});

export async function listQuestions(flowId: string): Promise<FlowQuestion[]> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/questions`);
  return QuestionsListResponseSchema.parse(res).questions;
}

export async function createQuestion(
  flowId: string,
  data: FlowQuestionCreate,
): Promise<FlowQuestion> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/questions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return QuestionResponseSchema.parse(res).question;
}

export async function updateQuestion(
  flowId: string,
  qid: string,
  data: FlowQuestionUpdate,
): Promise<FlowQuestion> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/questions/${qid}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return QuestionResponseSchema.parse(res).question;
}

export async function deleteQuestion(flowId: string, qid: string): Promise<void> {
  await request(`/api/admin/flows/${flowId}/questions/${qid}`, { method: 'DELETE' });
}

export async function reorderQuestions(flowId: string, orderedIds: string[]): Promise<void> {
  await request(`/api/admin/flows/${flowId}/questions/reorder`, {
    method: 'POST',
    body: JSON.stringify({ ordered_ids: orderedIds }),
  });
}

// ----- Outcomes -----

const OutcomesListResponseSchema = z.object({
  ok: z.literal(true),
  outcomes: z.array(FlowOutcomeSchema),
});
const OutcomeResponseSchema = z.object({
  ok: z.literal(true),
  outcome: FlowOutcomeSchema,
});

export async function listOutcomes(flowId: string): Promise<FlowOutcome[]> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/outcomes`);
  return OutcomesListResponseSchema.parse(res).outcomes;
}

export async function createOutcome(flowId: string, data: FlowOutcomeCreate): Promise<FlowOutcome> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/outcomes`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return OutcomeResponseSchema.parse(res).outcome;
}

export async function updateOutcome(
  flowId: string,
  oid: string,
  data: FlowOutcomeUpdate,
): Promise<FlowOutcome> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/outcomes/${oid}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
  return OutcomeResponseSchema.parse(res).outcome;
}

export async function deleteOutcome(flowId: string, oid: string): Promise<void> {
  await request(`/api/admin/flows/${flowId}/outcomes/${oid}`, { method: 'DELETE' });
}

// ----- Sprint 6: visual layout -----

export async function patchVisualLayout(flowId: string, layout: VisualLayout): Promise<void> {
  await request(`/api/admin/flows/${flowId}/visual-layout`, {
    method: 'PATCH',
    body: JSON.stringify(layout),
  });
}

// ----- Sprint 6: versioning -----

const FlowVersionSummarySchema = z.object({
  id: z.string().uuid(),
  version_number: z.number().int(),
  published_at: z.string(),
  published_by_email: z.string().nullable(),
  is_active: z.boolean(),
  summary: z
    .object({
      questions_count: z.number().int(),
      outcomes_count: z.number().int(),
      persona_hash: z.string(),
      language_default: z.string(),
      settings_hash: z.string(),
      boundaries_hash: z.string().optional(),
    })
    .nullable(),
});
export type FlowVersionSummary = z.infer<typeof FlowVersionSummarySchema>;

export async function listFlowVersions(flowId: string): Promise<FlowVersionSummary[]> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/versions`);
  return z.object({ ok: z.literal(true), versions: z.array(FlowVersionSummarySchema) }).parse(res)
    .versions;
}

const DiffSchema = z.object({
  persona: z.object({ changed_keys: z.array(z.string()) }),
  settings: z.object({ changed_keys: z.array(z.string()) }),
  boundaries: z.object({ added: z.array(z.string()), removed: z.array(z.string()) }),
  language_default: z.object({ from: z.string(), to: z.string() }).nullable(),
  questions: z.object({
    added: z.array(z.object({ id: z.string(), objective: z.string() })),
    removed: z.array(z.object({ id: z.string(), objective: z.string() })),
    modified: z.array(
      z.object({
        id: z.string(),
        objective: z.string(),
        changed_fields: z.array(z.string()),
      }),
    ),
  }),
  outcomes: z.object({
    added: z.array(z.object({ id: z.string(), name: z.string() })),
    removed: z.array(z.object({ id: z.string(), name: z.string() })),
    modified: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        changed_fields: z.array(z.string()),
      }),
    ),
  }),
});
export type FlowVersionsDiff = z.infer<typeof DiffSchema>;

export async function getFlowVersionsDiff(
  flowId: string,
  vAId: string,
  vBId: string,
): Promise<FlowVersionsDiff> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/versions/${vAId}/diff/${vBId}`);
  return z.object({ ok: z.literal(true), diff: DiffSchema }).parse(res).diff;
}

export async function rollbackFlowToVersion(
  flowId: string,
  versionId: string,
): Promise<{ rolled_back_to_version: number }> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/rollback/${versionId}`, {
    method: 'POST',
  });
  return z.object({ ok: z.literal(true), rolled_back_to_version: z.number().int() }).parse(res);
}

// ----- Sprint 6: test mode -----

const TestSessionStartResponseSchema = z.object({
  ok: z.literal(true),
  session_id: z.string().uuid(),
  flow_snapshot: z.object({
    id: z.string().uuid(),
    name: z.string(),
    language_default: z.string(),
    persona: z.unknown(),
    questions_count: z.number().int(),
    outcomes_count: z.number().int(),
  }),
});
export type TestSessionStartResponse = z.infer<typeof TestSessionStartResponseSchema>;

export async function startTestSession(
  flowId: string,
  flowVersionId?: string,
): Promise<TestSessionStartResponse> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/test-session/start`, {
    method: 'POST',
    body: JSON.stringify(flowVersionId ? { flow_version_id: flowVersionId } : {}),
  });
  return TestSessionStartResponseSchema.parse(res);
}

const TestSessionRowSchema = z.object({
  id: z.string().uuid(),
  status: z.string(),
  language_detected: z.string().nullable(),
  total_turns: z.number().int(),
  completeness_pct: z.number().int(),
  started_at: z.string(),
  completed_at: z.string().nullable(),
  duration_seconds: z.number().int().nullable(),
});
export type TestSessionRow = z.infer<typeof TestSessionRowSchema>;

export async function listTestSessions(flowId: string): Promise<TestSessionRow[]> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/test-sessions`);
  return z.object({ ok: z.literal(true), sessions: z.array(TestSessionRowSchema) }).parse(res)
    .sessions;
}

const TestSessionDetailSchema = z.object({
  ok: z.literal(true),
  session: TestSessionRowSchema.extend({
    language_script: z.string().nullable(),
    emergency_triggered: z.boolean(),
  }),
  messages: z.array(
    z.object({
      role: z.enum(['user', 'assistant', 'system']),
      content: z.string().nullable(),
      timestamp: z.string(),
      turn: z.number().int(),
    }),
  ),
});
export type TestSessionDetail = z.infer<typeof TestSessionDetailSchema>;

export async function getTestSession(
  flowId: string,
  sessionId: string,
): Promise<TestSessionDetail> {
  const res = await request<unknown>(`/api/admin/flows/${flowId}/test-sessions/${sessionId}`);
  return TestSessionDetailSchema.parse(res);
}

export async function deleteTestSession(flowId: string, sessionId: string): Promise<void> {
  await request(`/api/admin/flows/${flowId}/test-sessions/${sessionId}`, { method: 'DELETE' });
}

/** URL endpoint SSE per turno test session — il consumer apre fetch streaming manuale. */
export function testSessionTurnUrl(flowId: string, sessionId: string): string {
  return `/api/admin/flows/${flowId}/test-session/${sessionId}/turn`;
}

// avoid unused import elimination by re-exporting (potrebbe essere riusato in altre componenti)
export { VisualLayoutSchema };
